import { create } from 'zustand';
import {
  listBoats,
  putBoat,
  deleteBoat,
  getPrefs,
  putPrefs,
  type Boat,
} from '../../lib/db.js';
import { notifyMutation } from '../account/syncService.js';

interface BoatState {
  boats: Boat[];
  activeBoatId: string | null;
  loaded: boolean;
  load: () => Promise<void>;
  save: (boat: Boat) => Promise<void>;
  remove: (id: string) => Promise<void>;
  setActive: (id: string) => Promise<void>;
}

export const useBoatStore = create<BoatState>((set, get) => ({
  boats: [],
  activeBoatId: null,
  loaded: false,

  load: async () => {
    const [boats, prefs] = await Promise.all([listBoats(), getPrefs()]);
    // Default the active boat to the first if none/invalid.
    let activeBoatId = prefs.activeBoatId;
    if (!activeBoatId || !boats.some((b) => b.id === activeBoatId)) {
      activeBoatId = boats[0]?.id ?? null;
      if (activeBoatId !== prefs.activeBoatId) await putPrefs({ ...prefs, activeBoatId });
    }
    set({ boats, activeBoatId, loaded: true });
  },

  save: async (boat) => {
    await putBoat(boat);
    notifyMutation();
    const boats = await listBoats();
    let { activeBoatId } = get();
    if (!activeBoatId) {
      activeBoatId = boat.id;
      await putPrefs({ id: 'prefs', activeBoatId });
    }
    set({ boats, activeBoatId });
  },

  remove: async (id) => {
    await deleteBoat(id);
    notifyMutation();
    const boats = await listBoats();
    let { activeBoatId } = get();
    if (activeBoatId === id) {
      activeBoatId = boats[0]?.id ?? null;
      await putPrefs({ id: 'prefs', activeBoatId });
    }
    set({ boats, activeBoatId });
  },

  setActive: async (id) => {
    await putPrefs({ id: 'prefs', activeBoatId: id });
    set({ activeBoatId: id });
  },
}));

export function activeBoat(state: BoatState): Boat | null {
  return state.boats.find((b) => b.id === state.activeBoatId) ?? null;
}
